/* ayume© — « Le nécessaire de pose » (section ayume-essentials)
   three.js 0.169.0, copié en asset : assets/three.module.min.js (node_modules/three/build/three.module.min.js).
   Chargé seulement quand la section approche de l'écran. Repli statique (liste illustrée) sans WebGL 2
   ou avec prefers-reduced-motion. Le défilement au doigt n'est jamais bloqué (écouteurs passifs, aucun preventDefault). */

const HINTS_HOVER = { closed: 'Cliquez sur le sac', open: 'Cliquez pour tout ranger' };
const HINTS_TOUCH = { closed: 'Touchez le sac', open: 'Touchez pour tout ranger' };

function webglOK() {
  try { const c = document.createElement('canvas'); return !!c.getContext('webgl2'); }
  catch (e) { return false; }
}

function setup(root) {
  if (root.dataset.ayxReady) return;
  root.dataset.ayxReady = '1';
  const stage = root.querySelector('.ayx-stage');
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const goStatic = () => {
    root.classList.remove('is-3d', 'is-live');
    root.classList.add('is-static');
    stage.setAttribute('aria-hidden', 'true');
    stage.removeAttribute('role'); stage.removeAttribute('tabindex'); stage.removeAttribute('aria-pressed'); stage.removeAttribute('aria-label');
  };
  if (location.hash === '#statique' || reduceMotion || !webglOK()) { goStatic(); return; }
  root.classList.add('is-3d');
  const io = new IntersectionObserver((es) => {
    if (es.some(e => e.isIntersecting)) { io.disconnect(); boot(root, goStatic); }
  }, { rootMargin: '900px 0px' });
  io.observe(stage);
}

async function boot(root, goStatic) {
  try {
    const [THREE, logo] = await Promise.all([import(root.dataset.three), loadLogo(root)]);
    init(THREE, root, logo, goStatic);
  } catch (err) {
    console.warn('[ayume-essentials]', err);
    goStatic();
  }
}

// Le tracé du logo vient du SVG rendu par {% render 'ayume-logo' %} dans la section : aucune copie du tracé.
async function loadLogo(root) {
  const src = root.querySelector('.ayx-bag-logo svg');
  if (!src) return null;
  const svg = src.cloneNode(true);
  const vb = (svg.getAttribute('viewBox') || '').split(/[\s,]+/).map(Number);
  const w = vb[2] || parseFloat(svg.getAttribute('width')) || 2400;
  const h = vb[3] || parseFloat(svg.getAttribute('height')) || 545;
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.setAttribute('width', w); svg.setAttribute('height', h);
  svg.removeAttribute('class'); svg.setAttribute('style', 'color:#000');
  const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(svg)], { type: 'image/svg+xml' }));
  try {
    const img = new Image(); img.src = url; await img.decode();
    return { img, w, h };
  } catch (e) { return null; } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
}

/* ---------- utilitaires ---------- */
const clamp = (x, a, b) => Math.min(b, Math.max(a, x));
const lerp = (a, b, t) => a + (b - a) * t;
const sstep = (a, b, x) => { const t = clamp((x - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
function cubicBezier(x1, y1, x2, y2) {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sx = t => ((ax * t + bx) * t + cx) * t, sy = t => ((ay * t + by) * t + cy) * t;
  const dx = t => (3 * ax * t + 2 * bx) * t + cx;
  return x => {
    if (x <= 0) return 0; if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) { const e = sx(t) - x, d = dx(t); if (Math.abs(e) < 1e-5 || Math.abs(d) < 1e-6) break; t -= e / d; }
    if (t < 0 || t > 1) { let lo = 0, hi = 1; t = x; for (let i = 0; i < 24; i++) { if (sx(t) < x) lo = t; else hi = t; t = (lo + hi) / 2; } }
    return sy(t);
  };
}
const easeBrand = cubicBezier(.16, 1, .3, 1);
const easeFlight = cubicBezier(.5, 0, .16, 1);
const easeSoft = cubicBezier(.45, 0, .2, 1);
const easeIO = cubicBezier(.65, 0, .35, 1);

function hash2(x, y, s = 0) {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(s | 0, 1442695041)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
function vnoise(x, y, s = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
  const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
  const a = hash2(xi, yi, s), b = hash2(xi + 1, yi, s), c = hash2(xi, yi + 1, s), d = hash2(xi + 1, yi + 1, s);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}
function fbm(x, y, o = 4, s = 0) { let t = 0, a = .5, f = 1; for (let i = 0; i < o; i++) { t += a * vnoise(x * f, y * f, s + i * 17); f *= 2.03; a *= .5; } return t / (1 - Math.pow(.5, o)); }
function rng(seed) { return () => { seed |= 0; seed = seed + 0x6D2B79F5 | 0; let t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
function mkCanvas(w, h) { const c = document.createElement('canvas'); c.width = w; c.height = h; return [c, c.getContext('2d')]; }
/* ======================================================================= */
function init(THREE, root, logo, goStatic) {
  const stage = root.querySelector('.ayx-stage');
  const canvas = root.querySelector('.ayx-canvas');
  const labelsEl = root.querySelector('.ayx-labels');
  const hint = root.querySelector('.ayx-hint');
  const hintText = hint.querySelector('.ayx-hint-t');
  const canHover = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const HINTS = canHover ? HINTS_HOVER : HINTS_TOUCH;
  hintText.textContent = HINTS.closed;
  const META = {};
  root.querySelectorAll('.ayx-item[data-key]').forEach(li => {
    META[li.dataset.key] = [li.querySelector('.ayx-item-name').textContent.trim(), li.querySelector('.ayx-item-detail').textContent.trim()];
  });
  const makeLabel = (key) => {
    const [name, detail] = META[key] || ['', ''];
    const el = document.createElement('div'); el.className = 'ayx-label';
    el.innerHTML = '<div class="ayx-label-in"><span class="ayx-label-n"></span><span class="ayx-label-q"></span></div>';
    el.querySelector('.ayx-label-n').textContent = name; el.querySelector('.ayx-label-q').textContent = detail;
    labelsEl.appendChild(el); return el;
  };
  const {
    Vector2, Vector3, Quaternion, Euler, Color, Box3, Mesh, Group, Scene,
    PerspectiveCamera, OrthographicCamera, WebGLRenderTarget, CanvasTexture,
    MeshStandardMaterial, MeshPhysicalMaterial, MeshBasicMaterial, ShaderMaterial,
    PlaneGeometry, CylinderGeometry, LatheGeometry, ExtrudeGeometry, BoxGeometry, Shape, Path,
    DirectionalLight, Plane, Raycaster
  } = THREE;

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: false, powerPreference: 'high-performance' });
  } catch (e) { goStatic(); return; }
  if (!renderer.capabilities.isWebGL2) { renderer.dispose(); goStatic(); return; }
  const DPR = Math.min(window.devicePixelRatio || 1, 2);
  renderer.setPixelRatio(DPR);
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1.0;
  const BG = new THREE.Color(getComputedStyle(root).getPropertyValue('--ayx-bg').trim() || '#F1EFEA');
  renderer.setClearColor(BG, 1);
  const ext = renderer.extensions;
  const HALF = ext.has('EXT_color_buffer_half_float') || ext.has('EXT_color_buffer_float');

  const scene = new Scene();
  const PLASTER = BG;

  /* ---------- lumière de studio (environnement) ---------- */
  {
    const env = new Scene();
    const room = new Mesh(new BoxGeometry(1, 1, 1), new MeshBasicMaterial({ color: new Color(0.7, 0.695, 0.685), side: THREE.BackSide }));
    room.scale.set(60, 60, 60); env.add(room);
    const panel = (w, h, x, y, z, k) => {
      const m = new Mesh(new PlaneGeometry(w, h), new MeshBasicMaterial({ color: new Color(k, k, k * .985), side: THREE.DoubleSide }));
      m.position.set(x, y, z); m.lookAt(0, 0, 0); env.add(m);
    };
    panel(34, 18, -4, 10, 26, 2.6);    // grande boîte à lumière, en face, un peu haute
    panel(6, 40, -26, 4, 10, 3.2);     // bande gauche
    panel(6, 40, 26, 0, 8, 1.6);       // bande droite, plus douce
    panel(40, 8, 0, 28, 4, 2.2);       // plafond
    const pm = new THREE.PMREMGenerator(renderer);
    scene.environment = pm.fromScene(env, 0.035).texture;
    pm.dispose();
  }
  const key = new DirectionalLight(0xffffff, 1.0);
  key.position.set(-12, 22, 34);
  scene.add(key);
  const LIGHT_DIR = key.position.clone().normalize();

  /* ---------- textures générées ---------- */
  const texData = (c, repeat) => { const t = new CanvasTexture(c); t.colorSpace = THREE.NoColorSpace; if (repeat) { t.wrapS = t.wrapT = THREE.RepeatWrapping; } t.anisotropy = 4; return t; };
  const texColor = (c, repeat) => { const t = new CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; if (repeat) { t.wrapS = t.wrapT = THREE.RepeatWrapping; } t.anisotropy = 8; return t; };

  // papier : fibres
  const paperTex = (() => {
    const N = 256; const [c, x] = mkCanvas(N, N); const img = x.createImageData(N, N); const d = img.data;
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const n = fbm(i / 22, j / 22, 4, 3) * .6 + vnoise(i / 3, j / 3, 9) * .25 + hash2(i, j, 5) * .15;
      const v = clamp(128 + (n - .5) * 120, 0, 255); const k = (j * N + i) * 4; d[k] = d[k + 1] = d[k + 2] = v; d[k + 3] = 255;
    }
    x.putImageData(img, 0, 0);
    const r = rng(7); x.lineCap = 'round';
    for (let f = 0; f < 700; f++) {
      const px = r() * N, py = r() * N, a = r() * Math.PI, L = 4 + r() * 16;
      x.strokeStyle = r() > .5 ? 'rgba(255,255,255,.22)' : 'rgba(0,0,0,.16)'; x.lineWidth = .6 + r() * .7;
      x.beginPath(); x.moveTo(px, py); x.quadraticCurveTo(px + Math.cos(a) * L * .5 + (r() - .5) * 4, py + Math.sin(a) * L * .5 + (r() - .5) * 4, px + Math.cos(a) * L, py + Math.sin(a) * L); x.stroke();
    }
    return texData(c, true);
  })();

  // tampon ayume© (grain d'encre, bords irréguliers)
  const stampTex = (() => {
    const W = 1100, H = Math.round(W * (logo ? logo.h / logo.w : .227)) + 60;
    const [c, x] = mkCanvas(W, H);
    if (logo) { const lw = W - 60, lh = lw * logo.h / logo.w; x.drawImage(logo.img, (W - lw) / 2, (H - lh) / 2, lw, lh); }
    const img = x.getImageData(0, 0, W, H); const d = img.data;
    const a = new Float32Array(W * H); for (let i = 0; i < W * H; i++) a[i] = d[i * 4 + 3] / 255;
    const b = new Float32Array(W * H); const R = 2;
    for (let pass = 0; pass < 2; pass++) {
      const src = pass ? b : a, dst = pass ? a : b;
      for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
        let s = 0, n = 0;
        for (let k = -R; k <= R; k++) { const ii = pass ? i : clamp(i + k, 0, W - 1), jj = pass ? clamp(j + k, 0, H - 1) : j; s += src[jj * W + ii]; n++; }
        dst[j * W + i] = s / n;
      }
    }
    for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
      const k = j * W + i;
      const edge = a[k] + (fbm(i / 7, j / 7, 3, 11) - .5) * .3;
      let v = sstep(.26, .5, edge);
      v *= .9 + .1 * fbm(i / 16, j / 16, 3, 21);
      if (hash2(i >> 1, j >> 1, 31) > .992) v *= .55;
      d[k * 4] = d[k * 4 + 1] = d[k * 4 + 2] = 0; d[k * 4 + 3] = Math.round(v * 255);
    }
    x.putImageData(img, 0, 0);
    const t = texData(c); t.flipY = false; t.needsUpdate = true;
    return { tex: t, aspect: W / H };
  })();

  // bois (bâtonnet)
  const woodTex = (() => {
    const W = 64, H = 512; const [c, x] = mkCanvas(W, H); const img = x.createImageData(W, H); const d = img.data;
    const A = [226, 197, 160], B = [196, 150, 104];
    for (let j = 0; j < H; j++) for (let i = 0; i < W; i++) {
      const w = fbm(i / 5, j / 90, 4, 41);
      let s = .5 + .5 * Math.sin((i + w * 26) * .9);
      s = Math.pow(s, 3) * .7 + fbm(i / 2, j / 30, 2, 43) * .3;
      const t = clamp(s * .8 + (hash2(i, j, 44) - .5) * .12, 0, 1);
      const k = (j * W + i) * 4; for (let q = 0; q < 3; q++) d[k + q] = lerp(A[q], B[q], t); d[k + 3] = 255;
    }
    x.putImageData(img, 0, 0); return texColor(c, true);
  })();

  // grain de lime
  const gritTex = (() => {
    const N = 256; const [c, x] = mkCanvas(N, N); const img = x.createImageData(N, N); const d = img.data;
    for (let j = 0; j < N; j++) for (let i = 0; i < N; i++) {
      const v = clamp(128 + (hash2(i, j, 51) - .5) * 170 + (fbm(i / 9, j / 9, 3, 52) - .5) * 60, 0, 255);
      const k = (j * N + i) * 4; d[k] = d[k + 1] = d[k + 2] = v; d[k + 3] = 255;
    }
    x.putImageData(img, 0, 0); const t = texData(c, true); t.repeat.set(.35, .35); return t;
  })();

  // étiquette de la colle
  const glueLabelTex = (() => {
    const [c, x] = mkCanvas(640, 272);
    x.fillStyle = '#F4F3EF'; x.fillRect(0, 0, 640, 272);
    x.fillStyle = 'rgba(10,10,12,.08)'; x.fillRect(0, 0, 640, 3); x.fillRect(0, 269, 640, 3);
    return texColor(c);
  })();

  // feuille de gel pads
  const [gelMap, gelBump] = (() => {
    const W = 800, H = 880;
    const [c, x] = mkCanvas(W, H); const [cb, xb] = mkCanvas(W, H);
    const rr = (ctx, X, Y, w, h, r) => { ctx.beginPath(); ctx.moveTo(X + r, Y); ctx.arcTo(X + w, Y, X + w, Y + h, r); ctx.arcTo(X + w, Y + h, X, Y + h, r); ctx.arcTo(X, Y + h, X, Y, r); ctx.arcTo(X, Y, X + w, Y, r); ctx.closePath(); };
    x.clearRect(0, 0, W, H);
    rr(x, 4, 4, W - 8, H - 8, 26); x.fillStyle = 'rgba(247,248,249,.5)'; x.fill();
    x.lineWidth = 4; x.strokeStyle = 'rgba(185,190,196,.8)'; x.stroke();
    xb.fillStyle = '#000'; xb.fillRect(0, 0, W, H);
    const rows = 6, cols = 4, cw = (W - 70) / cols, ch = (H - 70) / rows;
    for (let r = 0; r < rows; r++) for (let q = 0; q < cols; q++) {
      const w = cw * (.84 - r * .035 - (q % 2) * .04), h = ch * (.8 - r * .02);
      const X = 35 + q * cw + (cw - w) / 2, Y = 35 + r * ch + (ch - h) / 2, rad = Math.min(w, h) * .42;
      rr(x, X, Y, w, h, rad); x.fillStyle = 'rgba(255,255,255,.62)'; x.fill();
      x.lineWidth = 3; x.strokeStyle = 'rgba(140,150,160,.55)'; x.stroke();
      rr(x, X + 7, Y + 7, w - 14, h - 14, rad * .8); x.lineWidth = 2; x.strokeStyle = 'rgba(255,255,255,.95)'; x.stroke();
      xb.save(); xb.filter = 'blur(4px)'; rr(xb, X + 3, Y + 3, w - 6, h - 6, rad); xb.fillStyle = '#fff'; xb.fill(); xb.restore();
      rr(xb, X + 6, Y + 6, w - 12, h - 12, rad * .9); xb.fillStyle = '#fff'; xb.fill();
    }
    return [texColor(c), texData(cb)];
  })();

  // lingettes (double sachet)
  const [wipeMap, wipeBump] = (() => {
    const W = 1240, H = 620;
    const [c, x] = mkCanvas(W, H); const [cb, xb] = mkCanvas(W, H);
    x.fillStyle = '#FBFBFA'; x.fillRect(0, 0, W, H);
    x.fillStyle = 'rgba(120,125,130,.10)'; x.fillRect(0, 0, W, 46); x.fillRect(0, H - 46, W, 46); x.fillRect(0, 0, 46, H); x.fillRect(W - 46, 0, 46, H); x.fillRect(W / 2 - 46, 0, 92, H);
    x.strokeStyle = 'rgba(90,95,100,.35)'; x.lineWidth = 3; x.strokeRect(1.5, 1.5, W - 3, H - 3);
    xb.fillStyle = '#808080'; xb.fillRect(0, 0, W, H);
    const M = 46;
    for (const [ctx, col] of [[x, 'rgba(0,0,0,.09)'], [xb, 'rgba(255,255,255,.9)']]) {
      ctx.strokeStyle = col; ctx.lineWidth = 2;
      for (let i = 0; i < W; i += 7) { ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, M); ctx.moveTo(i, H - M); ctx.lineTo(i, H); ctx.stroke(); }
      for (let j = 0; j < H; j += 7) { for (const x0 of [0, W / 2 - M, W / 2, W - M]) { ctx.beginPath(); ctx.moveTo(x0, j); ctx.lineTo(x0 + M, j); ctx.stroke(); } }
    }
    x.strokeStyle = 'rgba(10,10,12,.22)'; x.lineWidth = 2; x.setLineDash([9, 7]);
    x.beginPath(); x.moveTo(W / 2, 0); x.lineTo(W / 2, H); x.stroke(); x.setLineDash([]);
    x.fillStyle = 'rgba(10,10,12,.07)'; x.fillRect(M, M, W / 2 - 2 * M, 1.5); x.fillRect(W / 2 + M, M, W / 2 - 2 * M, 1.5);
    return [texColor(c), texData(cb)];
  })();

  /* ---------- matériaux ---------- */
  const M = {
    paperBack: new MeshStandardMaterial({ color: 0xF6F5F1, roughness: .92, bumpMap: paperTex, bumpScale: .25, side: THREE.DoubleSide }),
    clear: new MeshPhysicalMaterial({ color: 0xffffff, roughness: .015, metalness: 0, transmission: 1, thickness: 1.4, ior: 1.42,
      specularIntensity: .8, envMapIntensity: 1.1 }),
    clearEdge: new MeshPhysicalMaterial({ color: 0xffffff, roughness: .02, metalness: 0, transmission: 1, thickness: 1.0, ior: 1.49,
      attenuationColor: new Color(0xB9C6CB), attenuationDistance: 1.6, specularIntensity: 1, envMapIntensity: 1.3 }),
    clearThin: new MeshPhysicalMaterial({ color: 0xF4F7F8, roughness: .03, metalness: 0, transparent: true, opacity: .3, depthWrite: false, envMapIntensity: 1.6, clearcoat: 1, clearcoatRoughness: .04 }),
    clearCheap: new MeshStandardMaterial({ color: 0xEEF2F3, roughness: .1, transparent: true, opacity: .35 }),
    bottle: new MeshPhysicalMaterial({ color: 0xF6F6F4, roughness: .28, clearcoat: .6, clearcoatRoughness: .22 }),
    cap: new MeshStandardMaterial({ color: 0xFBFBFA, roughness: .42 }),
    glueLabel: new MeshStandardMaterial({ map: glueLabelTex, roughness: .62 }),
    wood: new MeshStandardMaterial({ map: woodTex, roughness: .78, bumpMap: woodTex, bumpScale: .6 }),
    file: new MeshStandardMaterial({ color: 0xEFEBE4, roughness: .96, bumpMap: gritTex, bumpScale: 1.1 }),
    gel: new MeshPhysicalMaterial({ map: gelMap, bumpMap: gelBump, bumpScale: 1.4, transparent: true, roughness: .12, metalness: 0,
      clearcoat: 1, clearcoatRoughness: .08, side: THREE.DoubleSide, depthWrite: false }),
    wipe: new MeshPhysicalMaterial({ map: wipeMap, bumpMap: wipeBump, bumpScale: .8, roughness: .4, clearcoat: .35, clearcoatRoughness: .3 }),
    backdrop: new MeshBasicMaterial({ color: BG, toneMapped: false }),
  };

  /* ---------- fond et ombres de contact ---------- */
  const backdrop = new Mesh(new PlaneGeometry(700, 700), M.backdrop);
  backdrop.position.z = -.02; scene.add(backdrop);

  const SH_W = 104, SH_H = 72, SH_FAR = 13;
  const rtSh = new WebGLRenderTarget(832, 576, { depthBuffer: false });
  const rtSh2 = new WebGLRenderTarget(832, 576, { depthBuffer: false });
  const shadowCam = new OrthographicCamera(-SH_W / 2, SH_W / 2, SH_H / 2, -SH_H / 2, 0, SH_FAR);
  shadowCam.position.set(0, 0, 0); shadowCam.lookAt(0, 0, 1);
  const shadowPlane = new Mesh(new PlaneGeometry(SH_W, SH_H), new MeshBasicMaterial({ map: rtSh.texture, transparent: true, depthWrite: false, toneMapped: false, color: 0x1a1612, opacity: .36, side: THREE.DoubleSide }));
  shadowPlane.scale.x = -1; shadowPlane.position.set(.3, -.85, .085); scene.add(shadowPlane);
  const shVert = 'void main(){ gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }';
  const shFrag = 'uniform float darkness; void main(){ gl_FragColor = vec4(0.0,0.0,0.0,(1.0-gl_FragCoord.z)*darkness); }';
  const shadowMats = {};
  const shadowMat = d => shadowMats[d] || (shadowMats[d] = new ShaderMaterial({ uniforms: { darkness: { value: d } }, vertexShader: shVert, fragmentShader: shFrag, transparent: true, depthTest: false, depthWrite: false }));

  /* ---------- flou (passes séparées) ---------- */
  const fsCam = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const blurMat = new ShaderMaterial({
    uniforms: { tMap: { value: null }, dir: { value: new Vector2() } },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy,0.0,1.0); }',
    fragmentShader: `uniform sampler2D tMap; uniform vec2 dir; varying vec2 vUv;
      void main(){
        vec4 c = texture2D(tMap, vUv) * 0.2270270270;
        c += texture2D(tMap, vUv + dir * 1.3846153846) * 0.3162162162;
        c += texture2D(tMap, vUv - dir * 1.3846153846) * 0.3162162162;
        c += texture2D(tMap, vUv + dir * 3.2307692308) * 0.0702702703;
        c += texture2D(tMap, vUv - dir * 3.2307692308) * 0.0702702703;
        gl_FragColor = c;
      }`,
    depthTest: false, depthWrite: false, toneMapped: false
  });
  const fsQuad = new Mesh(new PlaneGeometry(2, 2), blurMat); fsQuad.frustumCulled = false;
  const fsScene = new Scene(); fsScene.add(fsQuad);
  function blurPass(src, dst, dx, dy) {
    blurMat.uniforms.tMap.value = src.texture; blurMat.uniforms.dir.value.set(dx / src.width, dy / src.height);
    renderer.setRenderTarget(dst); renderer.render(fsScene, fsCam);
  }
  function blur(a, b, radii) { for (const r of radii) { blurPass(a, b, r, 0); blurPass(b, a, 0, r); } }

  const rtOpt = { type: HALF ? THREE.HalfFloatType : THREE.UnsignedByteType };
  const rtFrost = new WebGLRenderTarget(4, 4, { ...rtOpt, depthBuffer: true });
  const rtF1 = new WebGLRenderTarget(4, 4, { ...rtOpt, depthBuffer: false });
  const rtF2 = new WebGLRenderTarget(4, 4, { ...rtOpt, depthBuffer: false });

  /* ---------- le sac ---------- */
  const BW = 14, BH = 22, LIP = 1.4;
  const bag = new Group(); scene.add(bag);
  const frontGeo = new PlaneGeometry(BW, BH, 40, 64);
  const backGeo = new PlaneGeometry(BW, BH + LIP, 40, 64); backGeo.translate(0, LIP / 2, 0);
  frontGeo.userData.base = frontGeo.attributes.position.array.slice();
  backGeo.userData.base = backGeo.attributes.position.array.slice();
  const wrinkle = (x, y) => (fbm(x * .16 + 3.1, y * .16 + 1.7, 3, 61) - .5) * 1.0 + .35 * Math.sin(x * .9 + y * .35) * Math.sin(y * .7 - x * .2);
  const frontUni = {
    tBlur: { value: rtF1.texture }, tStamp: { value: stampTex.tex }, tPaper: { value: paperTex },
    uRes: { value: new Vector2(1, 1) }, uLight: { value: LIGHT_DIR }, uFrost: { value: .8 },
    uStamp: { value: new THREE.Vector4(.5, .49, .29, .29 * (BH / BW) / stampTex.aspect) }
  };
  const frontMat = new ShaderMaterial({
    uniforms: frontUni, side: THREE.DoubleSide,
    vertexShader: `varying vec2 vUv; varying vec3 vN; varying vec3 vW;
      void main(){ vUv = uv; vN = normalize(mat3(modelMatrix) * normal); vec4 w = modelMatrix * vec4(position,1.0); vW = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`,
    fragmentShader: `uniform sampler2D tBlur; uniform sampler2D tStamp; uniform sampler2D tPaper;
      uniform vec2 uRes; uniform vec3 uLight; uniform float uFrost; uniform vec4 uStamp;
      varying vec2 vUv; varying vec3 vN; varying vec3 vW;
      void main(){
        vec3 N = normalize(vN); if(!gl_FrontFacing) N = -N;
        vec3 V = normalize(cameraPosition - vW);
        vec3 L = normalize(uLight);
        float fib = texture2D(tPaper, vUv * vec2(2.6, 4.1)).r;
        float fib2 = texture2D(tPaper, vUv * vec2(9.0, 14.0) + 0.37).r;
        vec2 suv = gl_FragCoord.xy / uRes + N.xy * 0.018;
        vec3 behind = texture2D(tBlur, suv).rgb;
        float ndl = dot(N, L);
        float diff = 0.83 + 0.17 * ndl;
        vec3 paper = vec3(0.955, 0.950, 0.940) * diff * (0.965 + 0.07 * fib + 0.03 * fib2);
        float T = uFrost * (0.9 + 0.2 * fib);
        if(!gl_FrontFacing) T *= 0.6;
        vec3 col = mix(paper, behind, T);
        vec3 H = normalize(L + V);
        float nh = max(dot(N, H), 0.0);
        col += vec3(0.26 * pow(nh, 70.0) + 0.06 * pow(nh, 9.0));
        col += vec3(0.10 * pow(1.0 - max(dot(N, V), 0.0), 3.0));
        float edge = smoothstep(0.965, 1.0, abs(vUv.x * 2.0 - 1.0));
        col *= 1.0 - 0.07 * edge;
        col = mix(col, vec3(1.0), 0.35 * smoothstep(0.992, 1.0, vUv.y));
        vec2 l = vec2((vUv.y - (uStamp.y - uStamp.z * 0.5)) / uStamp.z, (vUv.x - (uStamp.x - uStamp.w * 0.5)) / uStamp.w);
        float ink = 0.0;
        if (gl_FrontFacing && l.x > 0.0 && l.x < 1.0 && l.y > 0.0 && l.y < 1.0) ink = texture2D(tStamp, l).a;
        vec3 inkCol = vec3(0.012, 0.012, 0.014) + 0.02 * fib;
        col = mix(col, inkCol, ink);
        gl_FragColor = vec4(col, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }`
  });
  const front = new Mesh(frontGeo, frontMat);
  const back = new Mesh(backGeo, M.paperBack);
  bag.add(back, front);

  const bagState = { B: -1, m: -1 };
  function updateBag(B, m) {
    if (Math.abs(B - bagState.B) < 1e-4 && Math.abs(m - bagState.m) < 1e-4) return;
    bagState.B = B; bagState.m = m;
    const fp = frontGeo.attributes.position.array, fb = frontGeo.userData.base;
    for (let i = 0; i < fp.length; i += 3) {
      const x = fb[i], y = fb[i + 1];
      const u = x / (BW / 2), v = (y + BH / 2) / BH;
      const across = sstep(0, .26, 1 - Math.abs(u)) * .85 + .15 * Math.pow(Math.max(0, 1 - u * u), .5);
      const along = sstep(0, .1, v) * (1 - .25 * sstep(.82, 1, v));
      const top = Math.pow(sstep(.62, 1, v), 2) * Math.pow(Math.max(0, 1 - u * u), .35);
      fp[i] = x;
      fp[i + 1] = y - m * 2.2 * top;
      fp[i + 2] = .1 + B * 2.45 * across * along + (.07 + .1 * B) * wrinkle(x, y) * across + m * 4.2 * top;
    }
    frontGeo.attributes.position.needsUpdate = true; frontGeo.computeVertexNormals();
    if (!backGeo.userData.done) {
      const bp = backGeo.attributes.position.array, bb = backGeo.userData.base;
      for (let i = 0; i < bp.length; i += 3) bp[i + 2] = .05 + .03 * wrinkle(bb[i] * 1.3, bb[i + 1] * 1.3);
      backGeo.attributes.position.needsUpdate = true; backGeo.computeVertexNormals(); backGeo.userData.done = true;
    }
  }

  /* ---------- les objets ---------- */
  function rrPath(p, w, h, r) {
    const x0 = -w / 2, x1 = w / 2, y0 = -h / 2, y1 = h / 2;
    p.moveTo(x0 + r, y0); p.lineTo(x1 - r, y0); p.absarc(x1 - r, y0 + r, r, -Math.PI / 2, 0, false);
    p.lineTo(x1, y1 - r); p.absarc(x1 - r, y1 - r, r, 0, Math.PI / 2, false);
    p.lineTo(x0 + r, y1); p.absarc(x0 + r, y1 - r, r, Math.PI / 2, Math.PI, false);
    p.lineTo(x0, y0 + r); p.absarc(x0 + r, y0 + r, r, Math.PI, Math.PI * 1.5, false);
    return p;
  }
  const items = [];
  function addItem(key, name, detail, group, opts = {}) {
    const holder = new Group(); holder.add(group); scene.add(holder);
    const it = { key, name, detail, holder, obj: group, k: 0, setK: opts.setK || (() => {}), shadow: opts.shadow ?? 1, meshes: [] };
    group.traverse(o => { if (o.isMesh) { it.meshes.push(o); o.userData.item = it; } });
    items.push(it); return it;
  }

  // Boîte transparente
  {
    const S = 7, R = 1.3, t = .2, hb = 1.0, hl = .44;
    const g = new Group();
    const ex = (shape, depth, bevel = .035) => new ExtrudeGeometry(shape, { depth, bevelEnabled: true, bevelThickness: bevel, bevelSize: bevel, bevelSegments: 2, curveSegments: 14 });
    const ring = (w, r, th) => { const s = rrPath(new Shape(), w, w, r); s.holes.push(rrPath(new Path(), w - 2 * th, w - 2 * th, r - th)); return s; };
    const floor = new Mesh(ex(rrPath(new Shape(), S, S, R), t), M.clear);
    const walls = new Mesh(ex(ring(S, R, t), hb - .04), M.clearEdge);
    g.add(floor, walls);
    const pivot = new Group(); pivot.position.set(0, S / 2 - .05, hb); g.add(pivot);
    const lid = new Group(); lid.position.set(0, -(S / 2 - .05), 0); pivot.add(lid);
    const plate = new Mesh(ex(rrPath(new Shape(), S + .06, S + .06, R + .03), t * .7), M.clearThin); plate.renderOrder = 10; plate.position.z = hl - t;
    const rim = new Mesh(ex(ring(S + .06, R + .03, t), hl - t - .02, .025), M.clearEdge);
    lid.add(plate, rim);
    for (const sx of [-1.7, 1.7]) {
      const k = new Mesh(new CylinderGeometry(.17, .17, 1.2, 20), M.clearEdge); k.rotation.z = Math.PI / 2; k.position.set(sx, S / 2 + .08, hb); g.add(k);
    }
    const latch = new Mesh(new BoxGeometry(1.1, .22, .3), M.clearEdge); latch.position.set(0, -S / 2 - .1, hb + .1); lid.add(latch);
    g.position.z = -(hb + hl) / 2;
    const wrap = new Group(); wrap.add(g);
    addItem('box', 'Boîte transparente', 'pour vos press-on', wrap, { shadow: .3, setK: k => { pivot.rotation.x = -.62 * k; } });
  }
  // Colle
  {
    const g = new Group();
    const P = (a) => a.map(([r, y]) => new Vector2(r, y));
    const body = new Mesh(new LatheGeometry(P([[0, 0], [.8, 0], [.93, .04], [.99, .14], [1, .3], [1, 3.35], [.97, 3.6], [.85, 3.78], [.62, 3.92], [.56, 4.02]]), 64), M.bottle);
    const capG = new CylinderGeometry(.68, .7, 1.1, 128, 1, false);
    { const p = capG.attributes.position; for (let i = 0; i < p.count; i++) { const x = p.getX(i), z = p.getZ(i), rr = Math.hypot(x, z); if (rr > .3) { const a = Math.atan2(z, x); const k = 1 + .022 * Math.max(0, Math.cos(a * 52)); p.setX(i, x * k); p.setZ(i, z * k); } } capG.computeVertexNormals(); }
    const cap = new Mesh(capG, M.cap); cap.position.y = 4.5;
    const shoulder = new Mesh(new LatheGeometry(P([[.68, 0], [.62, .07], [.4, .14], [0, .15]]), 48), M.cap); shoulder.position.y = 5.05;
    const nozzle = new Mesh(new CylinderGeometry(.14, .36, .85, 32), M.cap); nozzle.position.y = 5.6; nozzle.scale.set(1.5, 1, .62);
    const label = new Mesh(new CylinderGeometry(1.008, 1.008, 2.72, 64, 1, true, -Math.PI, Math.PI * 2), M.glueLabel); label.position.y = 1.82;
    g.add(body, cap, shoulder, nozzle, label);
    g.position.y = -3.0;
    const wrap = new Group(); wrap.add(g);
    addItem('glue', 'Colle pour press-on', '3 g', wrap);
  }
  // Gel pads : 4 feuilles
  {
    const g = new Group(); const sheets = [];
    for (let i = 0; i < 4; i++) {
      const m = new Mesh(new PlaneGeometry(8, 8.8), M.gel); m.position.z = i * .03; m.renderOrder = 2 + i; g.add(m); sheets.push(m);
    }
    addItem('gel', 'Gel pads', '4 feuilles', g, { shadow: .28, setK: k => {
      sheets.forEach((s, i) => { const j = i - 1.5; s.position.x = j * (.12 + .95 * k); s.position.y = -j * (.1 + .5 * k); s.rotation.z = j * (.012 + .07 * k); });
    } });
  }
  // Lingettes : 2 × 2
  {
    const g = new Group(); const pads = [];
    const geo = new PlaneGeometry(12.4, 6.2, 62, 31);
    { const p = geo.attributes.position; for (let i = 0; i < p.count; i++) {
      const x = p.getX(i), y = p.getY(i); const hc = x < 0 ? -3.1 : 3.1; const lx = (x - hc) / 3.1, ly = y / 3.1;
      const d = 1 - Math.max(Math.abs(lx), Math.abs(ly)); const b = Math.pow(sstep(.13, .5, d), .8);
      p.setZ(i, .2 * b + .012 * Math.sin(x * 9) * (1 - b)); } geo.computeVertexNormals(); }
    for (let i = 0; i < 2; i++) { const m = new Mesh(geo, M.wipe); m.position.z = i * .32; g.add(m); pads.push(m); }
    g.position.z = -.1;
    const wrap = new Group(); wrap.add(g);
    addItem('wipes', "Lingettes à l'alcool", '2 × 2', wrap, { setK: k => {
      pads[0].position.x = -.3 * k - .1; pads[0].position.y = .35 * k + .1; pads[0].rotation.z = -.02 - .03 * k;
      pads[1].position.x = .45 * k + .1; pads[1].position.y = -1.3 * k - .12; pads[1].rotation.z = .03 + .05 * k;
    } });
  }
  // Mini-lime
  {
    const L = 6.9, r1 = .96, r2 = .6; const al = Math.asin((r2 - r1) / L); const be = -al;
    const s = new Shape();
    s.moveTo(r1 * Math.cos(be), r1 * Math.sin(be));
    s.lineTo(r2 * Math.cos(be), L + r2 * Math.sin(be));
    s.absarc(0, L, r2, be, Math.PI - be, false);
    s.lineTo(-r1 * Math.cos(be), r1 * Math.sin(be));
    s.absarc(0, 0, r1, Math.PI - be, 2 * Math.PI + be, false);
    const geo = new ExtrudeGeometry(s, { depth: .2, bevelEnabled: true, bevelThickness: .04, bevelSize: .05, bevelSegments: 3, curveSegments: 32 });
    geo.computeBoundingBox(); const bb = geo.boundingBox; geo.translate(0, -(bb.min.y + bb.max.y) / 2, -(bb.min.z + bb.max.z) / 2);
    const m = new Mesh(geo, M.file);
    const g = new Group(); g.add(m);
    addItem('file', 'Mini-lime', 'à ongles', g);
  }
  // Repousse-cuticules en bois
  {
    const pts = [];
    const prof = [[0, 0], [.05, .12], [.12, .5], [.19, 1.1], [.235, 1.8], [.24, 2.4], [.24, 8.6], [.235, 9.1], [.2, 9.8], [.16, 10.4], [.12, 10.85], [.06, 11.02], [0, 11.05]];
    for (const [r, y] of prof) pts.push(new Vector2(r, y));
    const geo = new LatheGeometry(pts, 28);
    const p = geo.attributes.position;
    for (let i = 0; i < p.count; i++) { const y = p.getY(i); if (y > 8.6) { const k = (y - 8.6) / 2.45; p.setZ(i, p.getZ(i) * (1 - .62 * k)); p.setX(i, p.getX(i) * (1 + .5 * k)); } }
    geo.computeVertexNormals(); geo.translate(0, -5.5, 0);
    const m = new Mesh(geo, M.wood);
    const g = new Group(); g.add(m);
    addItem('stick', 'Repousse-cuticules', 'en bois', g);
  }

  const byKey = Object.fromEntries(items.map(i => [i.key, i]));

  /* ---------- positions : dans le sac, puis vue d'ensemble ---------- */
  const E = (x, y, z) => new Quaternion().setFromEuler(new Euler(x, y, z));
  const INSIDE = {
    gel: { p: [.5, -1.6], r: [0, 0, .06], lift: .12 },
    box: { p: [-.9, -5.3], r: [0, 0, -.07], lift: .1 },
    wipes: { p: [1.3, 3.9], r: [0, 0, Math.PI / 2 + .07], lift: .3 },
    glue: { p: [-2.3, 4.2], r: [0, 0, .36], lift: .22 },
    stick: { p: [.9, .6], r: [0, 0, -.52], lift: .55 },
    file: { p: [2.9, -1.2], r: [0, 0, -.2], lift: .5 },
  };
  const LAYOUTS = {
    land: { bag: { p: [0, .4], s: .8 }, items: {
      gel: { p: [-27.4, 4.9], r: [0, 0, .05], s: 1.1, row: 'lt' },
      box: { p: [-15.6, 5.3], r: [.12, 0, -.1], s: 1.16, row: 'lt' },
      wipes: { p: [-21.6, -8.8], r: [0, 0, .02], s: 1.04, row: 'lb' },
      glue: { p: [16.6, 5.4], r: [0, 0, -.62], s: 1.32, row: 'rt' },
      stick: { p: [28.2, 4.9], r: [0, 0, .58], s: 1.12, row: 'rt' },
      file: { p: [22.6, -8.4], r: [0, 0, 1.46], s: 1.25, row: 'rb' },
    } },
    port: { bag: { p: [0, .9], s: .46 }, items: {
      box: { p: [-9.4, 15.2], r: [.12, 0, -.1], s: .98, row: 1 },
      glue: { p: [9.4, 15.0], r: [0, 0, -.62], s: 1.18, row: 1 },
      gel: { p: [-9.6, .9], r: [0, 0, .05], s: .88, row: 2 },
      stick: { p: [9.4, .9], r: [0, 0, .62], s: .98, row: 2 },
      wipes: { p: [-9.6, -13.4], r: [0, 0, .02], s: .8, row: 3 },
      file: { p: [9.6, -13.2], r: [0, 0, 1.2], s: 1.08, row: 3 },
    } },
  };
  const tmpBox = new Box3();
  function restZ(it, q, s, k) {
    it.setK(k); it.holder.position.set(0, 0, 0); it.holder.quaternion.copy(q); it.holder.scale.setScalar(s); it.holder.updateMatrixWorld(true);
    tmpBox.setFromObject(it.holder); return tmpBox.clone();
  }
  for (const it of items) {
    const d = INSIDE[it.key]; const q = E(...d.r);
    const b = restZ(it, q, 1, 0);
    it.inside = { p: new Vector3(d.p[0], d.p[1], -b.min.z + d.lift), q, s: 1 };
  }
  let layout = null, layoutName = '';
  const bagOpenPose = { p: new Vector3(), s: 1 };
  const frames = { closed: { D: 60, c: new Vector3() }, open: { D: 90, c: new Vector3() } };
  function computeLayout(aspect) {
    const name = aspect < 1.1 ? 'port' : 'land';
    const L = LAYOUTS[name]; layoutName = name; layout = L;
    const all = new Box3();
    for (const it of items) {
      const d = L.items[it.key]; const q = E(...d.r);
      const b = restZ(it, q, d.s, 1);
      it.slot = { p: new Vector3(d.p[0], d.p[1], -b.min.z + .02), q, s: d.s };
      it.anchorY = d.p[1] + b.min.y - .6; it.row = d.row;
      const bb = b.clone(); bb.translate(new Vector3(d.p[0], d.p[1], 0)); bb.min.y -= (name === 'port' ? 3.4 : 3.0);
      all.union(bb);
    }
    const rows = {}; for (const it of items) rows[it.row] = Math.min(rows[it.row] ?? 1e9, it.anchorY);
    for (const it of items) it.anchorY = rows[it.row];
    bagOpenPose.p.set(L.bag.p[0], L.bag.p[1], 0); bagOpenPose.s = L.bag.s;
    all.union(new Box3(new Vector3(-BW / 2 * L.bag.s, -BH / 2 * L.bag.s + L.bag.p[1], 0), new Vector3(BW / 2 * L.bag.s, (BH / 2 + LIP) * L.bag.s + L.bag.p[1], 1)));
    const fit = (b, pad) => {
      const c = b.getCenter(new Vector3()); c.z = 0; const hw = (b.max.x - b.min.x) / 2, hh = (b.max.y - b.min.y) / 2;
      const halfH = Math.max(hh, hw / aspect) * (1 + pad); return { D: halfH / Math.tan(FOV * Math.PI / 360), c };
    };
    all.min.y -= name === 'port' ? 3.6 : 2.6;
    frames.open = fit(all, name === 'port' ? .07 : .07);
    const bagBox = new Box3(new Vector3(-BW / 2, -BH / 2, 0), new Vector3(BW / 2, BH / 2 + LIP, 2));
    frames.closed = fit(bagBox, name === 'port' ? .36 : .3);
    frames.closed.c.y -= .6;
    for (const it of items) it.setK(it.k);
  }

  /* ---------- caméra ---------- */
  const FOV = 26;
  const cam = new PerspectiveCamera(FOV, 1, 1, 600);

  /* ---------- étiquettes ---------- */
  for (const it of items) it.label = makeLabel(it.key);

  /* ---------- animation ---------- */
  const now = () => performance.now() / 1000;
  const prop = v => ({ v, segs: [] });
  function stepProp(p, t) {
    while (p.segs.length) {
      const s = p.segs[0]; if (t < s.t0) break;
      if (s.from == null) s.from = p.v;
      const k = clamp((t - s.t0) / s.dur, 0, 1); p.v = lerp(s.from, s.to, s.ease(k));
      if (k >= 1) p.segs.shift(); else break;
    }
  }
  const P = { m: prop(0), B: prop(1), S: prop(0), cam: prop(0), peek: prop(0) };
  let isOpen = false, busyUntil = 0;
  const OPEN_ORDER = ['glue', 'stick', 'wipes', 'file', 'gel', 'box'];
  const bez = (a, b, c, d, t, out) => {
    const u = 1 - t; return out.set(0, 0, 0).addScaledVector(a, u * u * u).addScaledVector(b, 3 * u * u * t).addScaledVector(c, 3 * u * t * t).addScaledVector(d, t * t * t);
  };
  for (const it of items) { it.cur = { p: it.inside.p.clone(), q: it.inside.q.clone(), s: 1 }; it.anim = null; it.hover = prop(0); }

  function setOpen(v, instant = false) {
    if (v === isOpen && !instant) return;
    isOpen = v;
    const t = now();
    stage.setAttribute('aria-pressed', String(v));
    stage.setAttribute('aria-label', v ? 'Ranger les objets dans le sac' : 'Ouvrir le sac et voir son contenu');
    hint.classList.add('is-swap');
    setTimeout(() => { hintText.textContent = v ? HINTS.open : HINTS.closed; hint.classList.remove('is-swap'); }, 350);
    labelsEl.classList.remove('has-hover');
    if (instant) {
      for (const it of items) {
        const tgt = v ? it.slot : it.inside; it.anim = null; it.cur.p.copy(tgt.p); it.cur.q.copy(tgt.q); it.cur.s = tgt.s; it.k = v ? 1 : 0;
      }
      P.m.v = v ? .45 : 0; P.B.v = v ? .3 : 1; P.S.v = v ? 1 : 0; P.cam.v = v ? 1 : 0; for (const k in P) P[k].segs = [];
      return;
    }
    let last = 0;
    if (v) {
      P.m.segs = [{ t0: t, dur: .7, to: 1, ease: easeBrand }, { t0: t + 1.7, dur: 1.2, to: .45, ease: easeSoft }];
      P.B.segs = [{ t0: t + 1.15, dur: 1.4, to: .3, ease: easeSoft }];
      P.S.segs = [{ t0: t + .9, dur: 1.7, to: 1, ease: easeBrand }];
      P.cam.segs = [{ t0: t + .05, dur: 2.5, to: 1, ease: easeSoft }];
      P.peek.segs = [{ t0: t, dur: .4, to: 0, ease: easeSoft }];
      OPEN_ORDER.forEach((key, j) => {
        const it = byKey[key]; const t0 = t + .22 + j * .14, dur = 1.9;
        const P0 = it.cur.p.clone(), P3 = it.slot.p.clone();
        const mx = clamp(it.inside.p.x * .5, -3.5, 3.5);
        const P1 = new Vector3(mx, 14.5, 2.6);
        const P2 = new Vector3(lerp(mx, P3.x, .82), P3.y + 6.5, 8.5);
        it.anim = { t0, dur, P0, P1, P2, P3, q0: it.cur.q.clone(), q1: it.slot.q, s0: it.cur.s, s1: it.slot.s, k0: it.k, k1: 1, kDelay: .55 };
        last = Math.max(last, t0 + dur);
      });
    } else {
      P.S.segs = [{ t0: t, dur: .9, to: 0, ease: easeSoft }];
      P.m.segs = [{ t0: t, dur: .6, to: 1, ease: easeBrand }];
      P.cam.segs = [{ t0: t + .1, dur: 2.2, to: 0, ease: easeSoft }];
      [...OPEN_ORDER].reverse().forEach((key, j) => {
        const it = byKey[key]; const t0 = t + .12 + j * .085, dur = 1.45;
        const P0 = it.cur.p.clone(), P3 = it.inside.p.clone();
        const mx = clamp(P3.x * .5, -3.5, 3.5);
        const P1 = new Vector3(P0.x, P0.y + 2.5, P0.z + 9);
        const P2 = new Vector3(mx, 19, 3.2);
        it.anim = { t0, dur, P0, P1, P2, P3, q0: it.cur.q.clone(), q1: it.inside.q, s0: it.cur.s, s1: 1, k0: it.k, k1: 0, kDelay: 0 };
        last = Math.max(last, t0 + dur);
      });
      P.B.segs = [{ t0: t + .25, dur: 1.1, to: 1, ease: easeSoft }];
      P.m.segs.push({ t0: last - .35, dur: .9, to: 0, ease: easeSoft });
    }
    busyUntil = last;
  }

  /* ---------- interactions ---------- */
  const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
  let down = null, hovered = null, bagHover = false, interacted = false;
  const ray = new Raycaster(); const ndc = new Vector2();
  function toggle() { interacted = true; setOpen(!isOpen); }
  stage.addEventListener('pointerdown', e => { down = { x: e.clientX, y: e.clientY, t: now() }; }, { passive: true });
  stage.addEventListener('pointerup', e => {
    if (!down) return;
    const moved = Math.hypot(e.clientX - down.x, e.clientY - down.y);
    if (moved < 12 && now() - down.t < .7) toggle();
    down = null;
  }, { passive: true });
  stage.addEventListener('pointercancel', () => { down = null; }, { passive: true });
  stage.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } });
  function setPointer(cx, cy) {
    const r = stage.getBoundingClientRect();
    pointer.tx = clamp(((cx - r.left) / r.width) * 2 - 1, -1, 1);
    pointer.ty = clamp(((cy - r.top) / r.height) * 2 - 1, -1, 1);
    ndc.set(pointer.tx, -pointer.ty);
  }
  stage.addEventListener('pointermove', e => { if (e.pointerType === 'mouse') { setPointer(e.clientX, e.clientY); pickHover(); } }, { passive: true });
  stage.addEventListener('pointerleave', e => { if (e.pointerType === 'mouse') { pointer.tx = pointer.ty = 0; setHover(null); bagHover = false; } }, { passive: true });
  stage.addEventListener('touchstart', e => { const t = e.touches[0]; if (t) setPointer(t.clientX, t.clientY); }, { passive: true });
  stage.addEventListener('touchmove', e => { const t = e.touches[0]; if (t) setPointer(t.clientX, t.clientY); }, { passive: true });
  stage.addEventListener('touchend', () => { pointer.tx *= .4; pointer.ty *= .4; }, { passive: true });

  const pickables = []; items.forEach(it => pickables.push(...it.meshes));
  function setHover(it) {
    if (hovered === it) return;
    if (hovered) { hovered.label.classList.remove('is-hover'); hovered.hover.segs = [{ t0: now(), dur: .7, to: 0, ease: easeBrand }]; }
    hovered = it;
    if (it) { it.label.classList.add('is-hover'); it.hover.segs = [{ t0: now(), dur: .9, to: 1, ease: easeBrand }]; }
    labelsEl.classList.toggle('has-hover', !!it);
  }
  function pickHover() {
    if (!canHover) return;
    ray.setFromCamera(ndc, cam);
    if (isOpen) {
      if (now() < busyUntil) { setHover(null); return; }
      const hit = ray.intersectObjects(pickables, false)[0];
      setHover(hit ? hit.object.userData.item : null);
    } else {
      const hit = ray.intersectObjects([front, back], false)[0];
      const h = !!hit;
      if (h !== bagHover) { bagHover = h; P.peek.segs = [{ t0: now(), dur: h ? .9 : .7, to: h ? 1 : 0, ease: easeBrand }]; }
    }
  }

  // indice au premier passage sur mobile : le sac s'entrouvre une fois
  let peeked = false;
  const ioPeek = new IntersectionObserver(es => {
    for (const e of es) if (e.isIntersecting && !peeked && !isOpen && !canHover) {
      peeked = true; const t = now();
      P.peek.segs = [{ t0: t + .5, dur: 1.0, to: 1, ease: easeBrand }, { t0: t + 1.9, dur: 1.2, to: 0, ease: easeSoft }];
    }
  }, { threshold: .6 });
  ioPeek.observe(stage);

  /* ---------- taille ---------- */
  let W = 1, H = 1;
  function resize() {
    W = Math.max(1, stage.clientWidth); H = Math.max(1, stage.clientHeight);
    renderer.setSize(W, H, false);
    cam.aspect = W / H; cam.updateProjectionMatrix();
    const bw = Math.round(W * DPR), bh = Math.round(H * DPR);
    frontUni.uRes.value.set(bw, bh);
    rtFrost.setSize(Math.max(2, bw >> 1), Math.max(2, bh >> 1));
    rtF1.setSize(Math.max(2, bw >> 2), Math.max(2, bh >> 2)); rtF2.setSize(Math.max(2, bw >> 2), Math.max(2, bh >> 2));
    const prev = layoutName; computeLayout(W / H);
    if (prev && prev !== layoutName && isOpen && !items.some(i => i.anim)) setOpen(true, true);
    if (!prev) for (const it of items) { it.setK(0); }
  }
  new ResizeObserver(resize).observe(stage);
  resize();
  for (const it of items) { it.cur.p.copy(it.inside.p); it.cur.q.copy(it.inside.q); it.setK(0); }

  /* ---------- rendu ---------- */
  const clipPlane = new Plane(new Vector3(0, 0, -1), 2.9);
  const casters = [];
  const addCaster = (mesh, d) => casters.push({ mesh, d });
  addCaster(front, .16);
  for (const it of items) for (const m of it.meshes) addCaster(m, it.shadow);
  const boxMeshes = byKey.box.meshes;
  const tmpV = new Vector3(), tmpQ = new Quaternion(), camTarget = new Vector3();
  let visible = true, running = false, lastT = now(), firstFrame = true, dead = false;

  function frame() {
    if (dead || !visible || document.hidden) { running = false; return; }
    requestAnimationFrame(frame);
    const t = now(); const dt = Math.min(.05, t - lastT); lastT = t;
    for (const k in P) stepProp(P[k], t);

    // caméra : respiration lente + suivi du doigt / de la souris + défilement
    const f = 1 - Math.exp(-dt * 3.2);
    pointer.x += (pointer.tx - pointer.x) * f; pointer.y += (pointer.ty - pointer.y) * f;
    const r = stage.getBoundingClientRect();
    const scrollK = clamp(((r.top + r.height / 2) / innerHeight - .5) * 2, -1, 1);
    const ck = P.cam.v;
    const D = lerp(frames.closed.D, frames.open.D, ck);
    camTarget.copy(frames.closed.c).lerp(frames.open.c, ck);
    const idle = 1;
    const yaw = idle * (.055 * Math.sin(t * .37) + .085 * pointer.x);
    const pitch = .13 + idle * (.03 * Math.sin(t * .29 + 1) + .05 * pointer.y + .07 * scrollK);
    cam.position.set(camTarget.x + D * Math.sin(yaw) * Math.cos(pitch), camTarget.y - D * Math.sin(pitch), D * Math.cos(yaw) * Math.cos(pitch));
    cam.up.set(Math.sin(idle * .012 * Math.sin(t * .23)), 1, 0);
    cam.lookAt(camTarget);

    // sac
    const peek = P.peek.v;
    updateBag(P.B.v, clamp(P.m.v + .32 * peek * (1 - P.cam.v), 0, 1.2));
    const bs = lerp(1, bagOpenPose.s, P.S.v);
    bag.scale.setScalar(bs); bag.position.copy(bagOpenPose.p).multiplyScalar(P.S.v);

    // objets
    for (const it of items) {
      stepProp(it.hover, t);
      if (it.anim) {
        const a = it.anim; const k = clamp((t - a.t0) / a.dur, 0, 1);
        const e = easeFlight(k);
        bez(a.P0, a.P1, a.P2, a.P3, e, it.cur.p);
        it.cur.q.slerpQuaternions(a.q0, a.q1, easeIO(clamp(k * 1.25 - .1, 0, 1)));
        it.cur.s = lerp(a.s0, a.s1, easeSoft(k));
        const kk = a.kDelay ? clamp((k - a.kDelay) / (1 - a.kDelay), 0, 1) : clamp(k * 2.2, 0, 1);
        it.k = lerp(a.k0, a.k1, easeBrand(kk));
        if (k >= 1) it.anim = null;
      }
      it.setK(it.k);
      it.holder.position.copy(it.cur.p);
      if (!isOpen && !it.anim) it.holder.position.y += peek * (1.2 + (it.inside.p.y + 10) * .06);
      const h = it.hover.v;
      it.holder.position.z += h * 1.4;
      it.holder.quaternion.copy(it.cur.q);
      if (h > 0) { tmpQ.setFromEuler(new Euler(.1 * h, -.08 * h, 0)); it.holder.quaternion.premultiply(tmpQ); }
      it.holder.scale.setScalar(it.cur.s * (1 + .035 * h));
      const on = isOpen && (!it.anim || (t - it.anim.t0) / it.anim.dur > .72);
      it.label.classList.toggle('is-on', on);
    }
    stage.classList.toggle('is-busy', t < busyUntil);

    // 1. ombres de contact
    backdrop.visible = false; shadowPlane.visible = false;
    for (const c of casters) { c.mat = c.mesh.material; c.mesh.material = shadowMat(c.d); }
    renderer.setRenderTarget(rtSh); renderer.setClearColor(0x000000, 0); renderer.clear();
    renderer.render(scene, shadowCam);
    for (const c of casters) c.mesh.material = c.mat;
    blur(rtSh, rtSh2, [2.2, 1.1]);

    // 2. ce qu'on voit à travers le papier givré
    backdrop.visible = true; shadowPlane.visible = true; front.visible = false;
    for (const m of boxMeshes) { m.userData.mat = m.material; m.material = M.clearCheap; }
    renderer.clippingPlanes = [clipPlane];
    renderer.setRenderTarget(rtFrost); renderer.setClearColor(PLASTER, 1); renderer.clear();
    renderer.render(scene, cam);
    renderer.clippingPlanes = [];
    for (const m of boxMeshes) m.material = m.userData.mat;
    front.visible = true;
    blurPass(rtFrost, rtF2, 1, 0); blurPass(rtF2, rtF1, 0, 1);
    blur(rtF1, rtF2, [1.0]);

    // 3. image finale
    renderer.setRenderTarget(null);
    renderer.render(scene, cam);

    // étiquettes
    for (const it of items) {
      tmpV.set(it.slot.p.x, it.anchorY, .2).project(cam);
      const x = (tmpV.x * .5 + .5) * W, y = (-tmpV.y * .5 + .5) * H;
      it.label.style.transform = `translate3d(${x.toFixed(1)}px,${y.toFixed(1)}px,0)`;
    }
    if (firstFrame) { firstFrame = false; requestAnimationFrame(() => root.classList.add('is-live')); }
  }
  function start() { if (!dead && !running && visible && !document.hidden) { running = true; lastT = now(); requestAnimationFrame(frame); } }
  new IntersectionObserver(es => { visible = es.some(e => e.isIntersecting); start(); }, { rootMargin: '120px 0px' }).observe(stage);
  document.addEventListener('visibilitychange', start);

  canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); dead = true; goStatic(); });
  start();
}

document.querySelectorAll('[data-ayx]').forEach(setup);
